import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { CopyIcon, CheckIcon } from '../icons';
import { useModalDrag } from '../../hooks/useModalDrag';

const REPORT_COMPONENTS = {
  table: p => <table className="md-table" {...p} />,
  th:    p => <th    className="md-th"    {...p} />,
  td:    p => <td    className="md-td"    {...p} />,
  tr:    p => <tr    className="md-tr"    {...p} />,
  h1:    p => <h1    className="md-h1"    {...p} />,
  h2:    p => <h2    className="md-h2"    {...p} />,
  h3:    p => <h3    className="md-h3"    {...p} />,
  pre:   ({ children, ...p }) => <pre className="md-pre" {...p}>{children}</pre>,
  code:  ({ className, children, ...p }) =>
    className
      ? <code className={`md-code-block ${className}`} {...p}>{children}</code>
      : <code className="md-code-inline" {...p}>{children}</code>,
  blockquote: p => <blockquote className="md-blockquote" {...p} />,
  a:     p => <a className="md-a" target="_blank" rel="noreferrer" {...p} />,
};


export default function IngestReportModal({ doc, onClose, onReassess }) {
  if (!doc) return null;

  const { boxRef, onDragStart } = useModalDrag();
  const [copied, setCopied] = useState(false);
  
  const handleCopy = () => {
    navigator.clipboard.writeText(doc.ingest_report).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleDownload = () => {
    const slug     = doc.filename.replace(/[^a-z0-9.]/gi, '-').toLowerCase();
    const filename = `ingest-report-${slug}.md`;
    const blob     = new Blob([doc.ingest_report], { type: 'text/markdown' });
    const url      = URL.createObjectURL(blob);
    const a        = document.createElement('a');
    a.href         = url;
    a.download     = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const [reassessing, setReassessing] = useState(false);
  const [reassessError, setReassessError] = useState(null);

  const handleReassess = async () => {
    setReassessing(true);
    setReassessError(null);
    try {
      await onReassess(doc.id);
    } catch (e) {
      setReassessError('Re-assessment failed — check server logs.');
    } finally {
      setReassessing(false);
    }
  };  

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div ref={boxRef} className="modal-box modal-box--report" onClick={e => e.stopPropagation()}>
        <div className="modal-header" onMouseDown={onDragStart}>
          <span className="modal-title">Ingest Report: {doc.filename}</span>
          <div className="modal-header-actions">
            {doc.ingest_report && (
              <>
                <button
                  className={`modal-action-btn ${copied ? 'modal-action-btn--done' : ''}`}
                  onClick={handleCopy}
                  title={copied ? 'Copied!' : 'Copy report'}
                >
                  {copied ? <CheckIcon size={11} /> : <CopyIcon size={11} />}
                  {copied ? 'copied' : 'copy'}
                </button>
                <button className="modal-action-btn" onClick={handleDownload} title="Download .md">
                  ↓ .md
                </button>
              </>
            )}

            {doc.status === 'ready' && onReassess && (
              <button
                className="modal-action-btn"
                onClick={handleReassess}
                disabled={reassessing}
                title="Re-run quality assessment on stored chunks"
              >
                {reassessing ? '…' : '↺ re-assess'}
              </button>
            )}
            {reassessError && (
              <span className="modal-reassess-error">{reassessError}</span>
            )}
          
            <button className="modal-close" onClick={onClose} title="Close">✕</button>
          </div>
        </div>
        <div className="modal-body modal-body--report">
          {doc.ingest_report
            ? (
              <div className="report-md-body">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={REPORT_COMPONENTS}>
                  {doc.ingest_report}
                </ReactMarkdown>
              </div>
            )
            : <p className="ingest-report-empty">No report available yet.</p>
          }
        </div>
      </div>
    </div>
  );
}